import { Suspense, useCallback, useEffect, useRef } from "react"
import { Controller, useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { TextArea } from "@/components/ui/input"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { DiagnosisCode } from "@/modules/patients/components/diagnosis-code.tsx"
import { useEditMode } from "@/modules/patients/detail/hooks/useEditMode"
import { MedicalHistory } from "@/modules/patients/detail/medical-history.tsx"
import { ReferralLetterCopilotButton } from "@/modules/patients/detail/referral/components/referral-letter-copilot-button"
import { useReferralStoreContext } from "@/modules/patients/detail/referral/store/useReferralStore"
import { usePatientEpisodesOfCareData } from "@/modules/patients/hooks/usePatientEpisodesOfCareData"
import { useProgressNoteData } from "@/modules/patients/hooks/useProgressNoteData"

type ReferralFormProps = {
  fetchKey?: number
  progressNoteId: string
}

export const ReferralForm = (props: ReferralFormProps) => {
  const { fetchKey, progressNoteId } = props
  const { t } = useTranslation()
  const isEditMode = useEditMode()
  const timeToTreat = useReferralStoreContext((state) => state.timeToTreat)

  const patientEpisodesOfCareData = usePatientEpisodesOfCareData()
  const { progressNoteAndParentData } = useProgressNoteData(patientEpisodesOfCareData, isEditMode)
  const copilotButtonRef = useRef<HTMLButtonElement>(null)

  const form = useFormContext()
  const {
    control,
    getValues,
    setValue,
    formState: { errors },
  } = form

  useEffect(() => {
    if (getValues("reasoning")) {
      return
    }

    if (copilotButtonRef.current) {
      const selectedProgressNote = progressNoteAndParentData.find(
        (note) => note.episodeOfCareProgressNoteData.id === progressNoteId,
      )?.episodeOfCareProgressNoteData

      if (selectedProgressNote && (selectedProgressNote.episodeOfCareId !== null || selectedProgressNote.anamnesis)) {
        copilotButtonRef.current.click()
      }
    }
  }, [getValues, progressNoteAndParentData, progressNoteId])

  const handleOnReferralLetterCopilotComplete = useCallback(
    (data: {
      text: string
      fileHash: string
      aiRequestDurationMs: number
      aiAnonymizationRequestDurationMs: number
      aiFeatureId: string
    }) => {
      setValue("reasoning", data.text, { shouldDirty: true })

      FaroInsightsWrapper.pushEvent("aifeature_referral_letter_copilot", {
        aiFeatureId: data.aiFeatureId,
        feature: "Referral Letter Copilot",
        fileHash: data.fileHash,
        aiRequestDurationMs: data.aiRequestDurationMs.toString(),
        aiAnonymizationRequestDurationMs: data.aiAnonymizationRequestDurationMs.toString(),
        sentAt: new Date().toISOString(),
      })
    },
    [setValue],
  )

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-4 self-start">
        <div className="text-on-surface leading-6 font-medium">{t("patient.referralLetter.reason")}</div>
        <ReferralLetterCopilotButton
          ref={copilotButtonRef}
          language="GERMAN"
          progressNoteId={progressNoteId}
          progressNoteAndParentData={progressNoteAndParentData}
          onComplete={handleOnReferralLetterCopilotComplete}
        />
      </div>
      <Controller
        control={control}
        name="diagnosisCode"
        render={({ field: { name, onChange, value }, fieldState: { invalid } }) => (
          <Suspense fallback={null}>
            <DiagnosisCode
              defaultValue={value}
              name={name}
              errors={errors}
              invalid={invalid}
              value={value}
              fetchKey={fetchKey}
              onSelect={onChange}
            />
          </Suspense>
        )}
      />
      <Controller
        name="reasoning"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextArea label={t("patient.referralLetter.reasoning")} rows={3} error={error?.message} {...field} />
        )}
      />
      <div className="grid gap-0.5">
        {timeToTreat && (
          <>
            <span className="text-on-surface-subdued truncate text-xs leading-4 tracking-[.25px]">
              {t("patient.referralLetter.timeToTreat")}
            </span>
            <div className="text-on-surface text-sm leading-5">
              {t(`episodeOfCare.progressNote.pointOfCare.timeToTreatOptionList.${timeToTreat.toLowerCase()}`)}
            </div>
          </>
        )}
      </div>
      <MedicalHistory />
    </div>
  )
}
